import React, { createContext, useContext, useEffect, useState } from 'react';
import { paymentsApiClient } from 'api';
import { Currency as ICurrency } from 'api/payments-client'; // Use ICurrency alias for clarity

// Defines the shape of the context value
interface ICurrencyContext {
  currencies: ICurrency[];
  loading: boolean;
  defaultCurrency: ICurrency | null;
}

// Creates the context with a default value
const CurrencyContext = createContext<ICurrencyContext | undefined>(undefined);

// Custom hook to use the currency context
export const useCurrency = (): ICurrencyContext => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Provider component to wrap the application
interface CurrencyProviderProps {
  children: React.ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  children,
}) => {
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [defaultCurrency, setDefaultCurrency] = useState<ICurrency | null>(
    null,
  );

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setLoading(true);
        const response =
          await paymentsApiClient.api.currencyControllerFindAllEnabled();
        setCurrencies(response.data);

        const chf = response.data.find((c) => c.isoCode === 'CHF');
        if (chf) {
          setDefaultCurrency(chf);
        } else if (response.data.length > 0) {
          setDefaultCurrency(response.data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrencies();
  }, []);

  const value = {
    currencies,
    loading,
    defaultCurrency,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
